import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";

/** Handles the redirect back from auth.emergentagent.com.
 * The URL contains `#session_id=<id>` — we POST it to the backend, then route to /app.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth() || {};
  const processed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const m = hash.match(/session_id=([^&]+)/) || search.match(/[?&]session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;

    // Surface OAuth provider errors (e.g. ?error=access_denied or #error=...)
    const errMatch = hash.match(/error(?:_description)?=([^&]+)/) || search.match(/[?&]error(?:_description)?=([^&]+)/);
    if (errMatch) {
      setError(decodeURIComponent(errMatch[1]).replace(/\+/g, " "));
      return;
    }

    if (!sessionId) {
      setError(
        "Couldn't complete Google sign-in. This usually means the domain isn't yet " +
        "approved for Google sign-in. You can sign in with email and password below, " +
        "or contact support to enable Google sign-in on this domain."
      );
      return;
    }

    (async () => {
      try {
        await api.post("/auth/google/session", { session_id: sessionId });
        // Clear the hash so the token doesn't linger in history
        window.history.replaceState(null, "", "/auth/callback");
        if (refresh) await refresh();
        navigate("/app", { replace: true });
      } catch (e) {
        const detail = e.response?.data?.detail;
        const status = e.response?.status;
        if (status === 502) {
          setError("Google sign-in is temporarily unavailable. Please try again in a moment, or sign in with email and password.");
        } else if (status === 401) {
          setError("Your Google sign-in session expired before we could verify it. Please try signing in again.");
        } else {
          setError(detail || "Sign-in failed. Please try again, or use email + password.");
        }
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center" data-testid="auth-callback-page">
        {error ? (
          <>
            <div className="text-rose-600 font-medium mb-2">{error}</div>
            <button
              className="text-sm text-orange-600 underline"
              onClick={() => navigate("/auth")}
              data-testid="callback-back-to-login"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 text-orange-500 animate-spin mx-auto mb-3" />
            <div className="text-slate-600">Signing you in…</div>
          </>
        )}
      </div>
    </div>
  );
}
